import { Maximize2, Minimize2 } from "lucide-react"
import { useId, useState } from "react"
import { type Control, useController } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { FieldShell } from "@/components/app/fields"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { estimateTokens } from "@/features/runs/format"
import type { AgentFormValues } from "@/features/studio/agent-form"
import { formatNumber } from "@/lib/format"
import { cn } from "@/lib/utils"

/** Large monospace system prompt editor with line/character/token counters. */
export function PromptEditor({ control }: { control: Control<AgentFormValues> }) {
  const { t } = useTranslation()
  const id = useId()
  const [expanded, setExpanded] = useState(false)
  const { field, fieldState } = useController({ control, name: "system_prompt" })
  const value = field.value ?? ""
  const lines = value ? value.split("\n").length : 0
  return (
    <FieldShell id={id} error={fieldState.error}>
      <div
        className={cn(
          "border-input focus-within:border-ring focus-within:ring-ring/50 dark:bg-input/30 overflow-hidden rounded-lg border transition-colors focus-within:ring-3",
          fieldState.invalid && "border-destructive",
        )}
      >
        <div className="bg-muted/40 text-muted-foreground flex items-center justify-between gap-2 border-b px-3 py-1.5 text-xs">
          <span className="tabular-nums">
            {t("studio.agents.prompt.stats", {
              count: lines,
              lines: formatNumber(lines),
              chars: formatNumber(value.length),
              tokens: formatNumber(estimateTokens(value)),
            })}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={() => setExpanded((e) => !e)}
            aria-label={expanded ? t("studio.agents.prompt.collapse") : t("studio.agents.prompt.expand")}
            aria-pressed={expanded}
          >
            {expanded ? <Minimize2 /> : <Maximize2 />}
          </Button>
        </div>
        <Textarea
          id={id}
          {...field}
          value={value}
          spellCheck={false}
          placeholder={t("studio.agents.prompt.placeholder")}
          aria-label={t("studio.agents.sections.instructions.title")}
          aria-invalid={fieldState.invalid || undefined}
          data-testid="system-prompt"
          className={cn(
            "resize-none rounded-none border-0 bg-transparent px-3 py-2.5 font-mono text-[0.8125rem] leading-relaxed shadow-none focus-visible:ring-0 dark:bg-transparent",
            expanded ? "h-[75dvh] [field-sizing:fixed]" : "max-h-[55dvh] min-h-72 overflow-y-auto",
          )}
        />
      </div>
    </FieldShell>
  )
}
